import { IconInCircle, Text, spaces, getToken } from '@electricjs/arc';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';

import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import {
  DeviceIssuesData,
  DeviceIssueStatus,
  getDeviceIssueStatus,
} from '@/components/Assets/AssetDetails/DeviceSecurityPanel/DeviceSecurityPanel';
import { UnavailableInfoChip } from '@/components/Assets/AssetDetails/UnavailableInfoChip';
import { DeviceIssueStatusChip } from './DeviceIssueStatusChip';
import styled from 'styled-components';

const StyledIconInCircle = styled(IconInCircle)`
  min-width: ${getToken('space_2xl', 'spacing')};
  min-height: ${getToken('space_2xl', 'spacing')};
`;

const getSecurityPoliciesColumns = (t: TFunction) => {
  const columnHelper = createColumnHelper<DeviceIssuesData>();
  const { space_s } = spaces;
  const deviceIssueStatus = getDeviceIssueStatus(t);

  return [
    columnHelper.display({
      header: t('deviceSecurityPanel.policy'),
      id: 'name',
      cell: data => (
        <TableCellFlexContainer>
          <StyledIconInCircle icon={data.row.original.icon} mr={space_s} />
          <Text variant="body-2">{data.row.original.name}</Text>
        </TableCellFlexContainer>
      ),
    }),
    columnHelper.accessor('status', {
      header: t('deviceSecurityPanel.status'),
      id: 'status',
      sortingFn: 'alphanumeric',
      cell: data => {
        // Render the "UnavailableInfoChip" if the "Check issue"
        // feature is disabled for the item report.
        if (data.row.original.disabled) {
          return (
            <TableCellFlexContainer>
              <UnavailableInfoChip />
            </TableCellFlexContainer>
          );
        }

        const statusChipMap = {
          [DeviceIssueStatus.Unknown]: (
            <DeviceIssueStatusChip
              status={DeviceIssueStatus.Unknown}
              text={deviceIssueStatus.Unknown}
            />
          ),
          [DeviceIssueStatus.Issue]: (
            <DeviceIssueStatusChip
              status={DeviceIssueStatus.Issue}
              text={deviceIssueStatus.Issue}
            />
          ),
          [DeviceIssueStatus.Enabled]: (
            <DeviceIssueStatusChip
              status={DeviceIssueStatus.Enabled}
              text={deviceIssueStatus.Enabled}
            />
          ),
          [DeviceIssueStatus.Failed]: (
            <DeviceIssueStatusChip
              status={DeviceIssueStatus.Failed}
              text={deviceIssueStatus.Failed}
            />
          ),
        };

        const status = data.row.original.status;

        return (
          <TableCellFlexContainer>
            {Object.keys(statusChipMap).includes(status) ? (
              statusChipMap[status as keyof typeof statusChipMap]
            ) : (
              <DeviceIssueStatusChip
                status={DeviceIssueStatus.Unknown}
                text={deviceIssueStatus.Unknown}
              />
            )}
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.display({
      header: t('deviceSecurityPanel.recommendedAction'),
      id: 'actions',
      meta: { maxWidth: 220 },
      cell: data => {
        const { hasData, hasIssue, action } = data.row.original;

        return (
          <TableCellFlexContainer justifyContent="flex-start">
            {hasData !== undefined && hasIssue ? (
              action?.()
            ) : (
              <Text variant="body-2">--</Text>
            )}
          </TableCellFlexContainer>
        );
      },
    }),
  ];
};

export default getSecurityPoliciesColumns;
